import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { keyboardBlur } from '@helpers/input-helper'
import { useApolloClient } from '@apollo/client'
import { getLatLng } from '@helpers/geo-location'
import useStoreLocatorContext from '@context/storeLocatorContext/useStoreLocatorContext'
import { Button, OutlinedInput, Stack, useTheme, useMediaQuery, styled, Typography } from '@mui/material'

const propOptions = { shouldForwardProp: prop => prop !== 'isMobile' }

const StyledSearchBar = styled('div')(() => ({}))

const StyledTitle = styled(Typography)({
  fontWeight: 400,
  fontSize: 19,
  mb: 1,
  '&:first-letter': {
    textTransform: 'capitalize',
  },
})

const StyledSearchButton = styled(
  Button,
  propOptions,
)(({ isMobile }) => ({
  marginTop: '0!important',
  padding: '0.5rem 2rem',
  fontSize: '15px',
  maxWidth: '100px',
  ...(isMobile && {
    fontSize: '0.8rem',
  }),
}))

const StyledInputSearch = styled(OutlinedInput)(() => ({
  padding: 0,
  'input[class*=MuiInput]': {
    padding: '7px 12px !important',
  },
}))

const onEnterPress = (e, changeAddress, isEmpty) => {
  if (e.keyCode === 13 && e.shiftKey === false && !isEmpty) {
    changeAddress()
    keyboardBlur(e)
  }
}

const SearchBar = ({ className, title = 'Find Stores Near You' }) => {
  const [invalidAddress, setInvalidAddress] = useState(false)
  const [addressInput, setAddressInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const mapInstance = useStoreLocatorContext(state => state.mapInstance)
  const setCurrentPosition = useStoreLocatorContext(state => state.setCurrentPosition)
  const setSearchedLocation = useStoreLocatorContext(state => state.setSearchedLocation)
  const apolloClient = useApolloClient()
  const isEmpty = !addressInput || !addressInput.trim()

  const updateInputAddressValue = evt => {
    setAddressInput(evt.target.value)
    setInvalidAddress(false)
  }

  const changeAddress = async defaultAddress => {
    setIsLoading(true)
    const addressData = defaultAddress || addressInput
    const location = await getLatLng(apolloClient, addressData)
    if (location?.lat) {
      setSearchedLocation(location)
      mapInstance.setCenter({ lat: location.lat, lng: location.lng })
      setCurrentPosition({ lat: location.lat, lng: location.lng })
    } else {
      setInvalidAddress(true)
    }
    setIsLoading(false)
  }

  return (
    <StyledSearchBar className={className}>
      <StyledTitle variant="body2">{title}</StyledTitle>
      {invalidAddress && <p className="invalid-message">*Please enter a valid Address</p>}
      <Stack direction="row" spacing={2}>
        <StyledInputSearch
          tabIndex={0}
          placeholder={isMobile ? 'City & State or ZIP' : 'City & State or ZIP Code'}
          size="small"
          fullWidth
          data-testid="search-store-locator"
          name="search-store-locator"
          value={addressInput}
          onChange={evt => updateInputAddressValue(evt)}
          onKeyDown={e => onEnterPress(e, changeAddress, isEmpty)}
          autoComplete="off"
        />
        <StyledSearchButton
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          sx={{ mt: 1 }}
          value="Change address"
          gtm-category="see in store"
          gtm-action="change address"
          onClick={() => changeAddress()}
          isMobile={isMobile}
          disabled={isEmpty || isLoading || !mapInstance?.loaded}
        >
          UPDATE
        </StyledSearchButton>
      </Stack>
    </StyledSearchBar>
  )
}

SearchBar.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
}

export default SearchBar
